import { FC, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { deleteData, fetchData, getTranslatedValue, toasterrormsg, toastsuccessmsg } from '../../../../reusable'
import { PageTitle } from '../../../../_metronic/layout/core'
import { KTIcon } from '../../../../_metronic/helpers'
import { ToastContainer } from 'react-toastify'
import DataTable from '../../../components/AppDataTable'
import Loader from '../../component/Loader'
import clsx from 'clsx'
import moment from 'moment'
import Swal from 'sweetalert2'
import { Tr } from '../../../../_metronic/i18n/Tr'
import { useLang } from '../../../../_metronic/i18n/Metronici18n'
import {
  advertisementStatuses,
  sponserPauseResumeApiCall,
  formatDuration,
  getStatusBadgeClass,
  pausableStatuses,
} from '../bannerAdvertisement/AddAdvertisement.constants'

const useDebounce = (value: string, delay = 500) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

export const ViewSponsoreAds: FC = () => {
  const t = Tr()
  const navigate = useNavigate();
  const location = useLocation();
  const lang = useLang();

  var vendorId = ''
  const vendorData = sessionStorage.getItem("vendor")
  if (vendorData) {
    var vendor = JSON.parse(vendorData)
    vendorId = vendor.vendorId
  }

  const [advertisementList, setAdvertisementList] = useState<any[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [statusTab, setStatusTab] = useState('')
  const [limit, setLimit] = useState(10)
  const [offset, setOffset] = useState(0)
  const [totalRecords, setTotalRecords] = useState(0)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)

  const debouncedSearch = useDebounce(searchTerm)

  useEffect(() => {
    document.title = 'Insayr | Banner Advertisements'
  }, [])

  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const paymentStatusParam = params.get('payment_status')
    const encodedSponserIdRaw = localStorage.getItem('clickPaymentSponserId')
    const encodedContextRaw = localStorage.getItem('clickPaymentSponserContext')
    const decodeSafe = (val: string) => { try { return atob(val) } catch { return val } }
    const adsId = params.get('transaction_param') || (encodedSponserIdRaw ? decodeSafe(encodedSponserIdRaw) : null)
    const paymentContextRaw = encodedContextRaw ? decodeSafe(encodedContextRaw) : null
    localStorage.removeItem('clickPaymentSponserId')
    localStorage.removeItem('clickPaymentSponserContext')

    if (paymentStatusParam === null) return

    // Click gateway returns payment_status as a number:
    // status < 0 – Error
    // status = 0 – Payment is created
    // status = 1 – Payment is being processed
    // status = 2 – Payment is made successfully
    const clickPaymentStatus = Number(paymentStatusParam)

    // Pass Click's payment_status directly to the API
    if (adsId) {
      const ctx = paymentContextRaw ? JSON.parse(paymentContextRaw) : null
      fetchData('public/addeditadspayment', {
        isPurchase: 1,
        vendorId: ctx?.vendorId ?? Number(vendorId),
        adsId: Number(adsId),
        price: ctx?.price ?? '0',
        paymentMethod: 'click',
        transactionNumber: ctx?.transactionNumber ?? adsId,
        paymentStatus: clickPaymentStatus,
        duration: ctx?.duration ?? '1',
        durationType: ctx?.durationType ?? 'WEEK',
      }).catch((err: any) => {
        console.error('Failed to update payment status', err)
      })
    }

    if (clickPaymentStatus === 2) {
      toastsuccessmsg(t('Payment successful! Your ad has been submitted for review.'))
      navigate(location.pathname, { replace: true })
    } else if (clickPaymentStatus < 0) {
      toasterrormsg(t('Payment failed. Please try again.'))
      navigate('/addsponsuredadvertisment', {
        replace: true,
        state: { adsId, tab: 4, paymentFailed: true },
      })
    } else if (clickPaymentStatus === 1) {
      toastsuccessmsg(t('Payment is being processed. Your ad will be updated shortly.'))
      navigate(location.pathname, { replace: true })
    } else {
      // status = 0: payment created but not yet confirmed
      navigate(location.pathname, { replace: true })
    }
  }, [])

  // Fetch advertisements list from API
  const fetchAdvertisementsApiCall = async (status: any = statusTab) => {
    setIsLoading(true)
    var param: any = {
      vendorId: String(vendorId),
      limit: limit,
      offset: offset,
      search: debouncedSearch,
    }
    if (status !== '') {
      param.status = status
    }
    if (startDate) {
      param.startDate = startDate
    }
    if (endDate) {
      param.endDate = endDate
    }

    var res = await fetchData('public/getvendorsponserlist', param)

    if (res.status == '200') {
      setIsLoading(false)
      var data = res?.data?.data
      setAdvertisementList(data)
      setTotalRecords(res?.data?.totalRecords ?? data?.length ?? 0)
    } else {
      setIsLoading(false)
      toasterrormsg(res.message)
    }
  }

  // Delete sponsored ad API call
  const deleteSponserApiCall = (id: any) => {
    Swal.fire({
      title: t('Are you sure?'),
      text: t('You want to delete this sponsored ad'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('Yes'),
      cancelButtonText: t('No'),
    }).then(async (result) => {
      if (result.isConfirmed) {
        var param = {
          vendorId: vendorId,
          sponserId: id
        }
        var res = await deleteData('public/deletesponser', param)

        if (res.status == '200') {
          toastsuccessmsg(t('Sponsored ad deleted successfully.'))
          // Re-run the list effect so the refetch uses the current tab/paging state
          setRefreshKey((key) => key + 1)
        } else {
          toasterrormsg(res.message)
        }
      }
    })
  }

  // Pause an approved sponsored ad / resume a paused one
  const pauseResumeSponserApiCall = (id: any, currentStatus: string) => {
    const isPausing = currentStatus !== 'PAUSED'
    Swal.fire({
      title: t('Are you sure?'),
      text: isPausing
        ? t('You want to pause this sponsored ad')
        : t('You want to resume this sponsored ad'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('Yes'),
      cancelButtonText: t('No'),
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true)
        var res = await sponserPauseResumeApiCall(vendorId, id, isPausing ? 'PAUSED' : 'ACTIVATED')
        setIsLoading(false)

        if (res.status == '200') {
          toastsuccessmsg(
            isPausing
              ? t('Sponsored ad paused successfully.')
              : t('Sponsored ad resumed successfully.')
          )
          // Re-run the list effect so the refetch uses the current tab/paging state
          setRefreshKey((key) => key + 1)
        } else {
          toasterrormsg(res.message)
        }
      }
    })
  }

  // Search data functionality
  const searchData = async (text: any) => {
    setSearchTerm(text)
    setOffset(0)
  }

  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(totalRecords / limit)
    if (newPage === 1) {
      setOffset(0)
    } else if (newPage === totalPages) {
      setOffset((totalPages - 1) * limit)
    } else {
      setOffset((newPage - 1) * limit)
    }
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setLimit(newRowsPerPage)
    setOffset(0)
  }


  const getStatusDisplay = (status: string) => {
    return advertisementStatuses.find((item) => item.api === status)?.display || status || '-'
  }

  const handleEdit = (row: any) => {
    navigate("/addsponsuredadvertisment", { state: { adsData: row, adsId: row.sponserId } })
  }

  // Drafts (and rows with no status yet) are the only ones a vendor can still edit or delete
  const editableStatuses = ['DRAFT', null]

  // Define DataTable columns — order follows the agreed table structure:
  // Property Name | Status | Property Type | Duration | Price per day | Total price |
  // Start Date | End Date | Created | Action
  const column: any = useMemo(
    () => [
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Property Name')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {getTranslatedValue(lang, row, '_name') || row.en_name || '-'}
            </div>
          </div>
        ),
        sortable: false,
        filter: true,
        width: '180px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Status')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <span
              className={`btn btn-sm fw-bold w-auto min-w-100px pointer-events-none ${getStatusBadgeClass(
                row.status
              )}`}
            >
              {t(getStatusDisplay(row.status))}
            </span>
          </div>
        ),
        sortable: false,
        filter: true,
        width: '160px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Property Type')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5 text-capitalize'>{row.actionType || '-'}</div>
          </div>
        ),
        sortable: false,
        filter: true,
        width: '150px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Duration')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {formatDuration(row?.duration, row?.durationType, lang)}
            </div>
          </div>
        ),
        sortable: false,
        filter: true,
        width: '130px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Per Day Price')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.perDayPrice != null ? row.perDayPrice : '-'}</div>
          </div>
        ),
        sortable: false,
        filter: true,
        width: '130px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Total Price')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.price != null ? row.price : '-'}</div>
          </div>
        ),
        sortable: false,
        filter: true,
        width: '130px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Start Date')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {row.startDate ? moment(row.startDate).format('DD.MM.YYYY') : '-'}
            </div>
          </div>
        ),
        sortable: false,
        filter: true,
        width: '130px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('End Date')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {row.endDate ? moment(row.endDate).format('DD.MM.YYYY') : '-'}
            </div>
          </div>
        ),
        sortable: false,
        filter: true,
        width: '130px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Created')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {row.created ? moment(row.created).format('DD.MM.YYYY') : '-'}
            </div>
          </div>
        ),
        sortable: false,
        filter: true,
        width: '130px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Action')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <button
              className='btn btn-sm btn-icon btn-color-info btn-active-light-info me-2'
              data-toggle='tooltip'
              title={t('View')}
              onClick={() => navigate('/viewsponsuredadvertismentdetail', { state: row })}
            >
              <KTIcon iconName='eye' className='fs-2x text-info' />
            </button>
            {editableStatuses.includes(row.status) && (
              <>
                <button
                  className='btn btn-sm btn-icon btn-color-primary btn-active-light-primary me-2'
                  data-toggle='tooltip'
                  title={t('Edit')}
                  onClick={() => handleEdit(row)}
                >
                  <KTIcon iconName='pencil' className='fs-2x text-primary' />
                </button>
                <button
                  className='btn btn-sm btn-icon btn-color-danger btn-active-light-danger me-2'
                  data-toggle='tooltip'
                  title={t('Delete')}
                  onClick={() => deleteSponserApiCall(row.sponserId)}
                >
                  <KTIcon iconName='trash' className='fs-2x text-danger' />
                </button>
              </>
            )}
            {pausableStatuses.includes(row.status) && (
              <button
                className={clsx('btn btn-sm fw-bold', {
                  'btn-light-warning': row.status !== 'PAUSED',
                  'btn-light-success': row.status === 'PAUSED',
                })}
                data-toggle='tooltip'
                title={row.status === 'PAUSED' ? t('Resume') : t('Pause')}
                onClick={() => pauseResumeSponserApiCall(row.sponserId, row.status)}
              >
                {row.status === 'PAUSED' ? t('Resume') : t('Pause')}
              </button>
            )}
          </div>
        ),
        sortable: false,
        width: '260px',
      },
    ],
    [lang, t]
  )

  useEffect(() => {
    fetchAdvertisementsApiCall(statusTab)
  }, [limit, offset, debouncedSearch, statusTab, startDate, endDate, refreshKey])

  return (
    <>
      {isLoading && <Loader />}
      <ToastContainer />
      <PageTitle breadcrumbs={[]}>{t('View Advertisements')}</PageTitle>
      {
        <div className='card card-flush py-4'>
          <div className='card-body pt-0'>
            <div className='d-flex justify-content-between align-items-center mb-5'>
              <div className='d-flex align-items-center gap-3 mt-2'>
                <span
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '10px',
                    background: '#e8f4fd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <svg width='20' height='20' viewBox='0 0 24 24' fill='none'>
                    <rect x='2' y='4' width='20' height='14' rx='2' stroke='#009ef7' strokeWidth='1.8' />
                    <path d='M2 9h20' stroke='#009ef7' strokeWidth='1.8' />
                    <path d='M6 14h4M6 17h2' stroke='#009ef7' strokeWidth='1.8' strokeLinecap='round' />
                  </svg>
                </span>
                <div>
                  <h2 className='mb-0'>{t('Search Ads')}</h2>
                  <p className='text-muted fs-7 mb-0'>{t('Manage your sponsored advertisements')}</p>
                </div>
              </div>
              <Link
                to='/addsponsuredadvertisment'
                state={{ isNew: true }}
                className='btn btn-primary'
              >
                + {t('Create New Ad')}
              </Link>
            </div>

            <div className='card card-custom'>
              <div className='card-header card-header-stretch overflow-auto'>
                <ul
                  className='nav nav-stretch nav-line-tabs fw-semibold border-transparent flex-nowrap'
                  role='tablist'
                >
                  <li className='nav-item'>
                    <a
                      className={clsx(`nav-link cursor-pointer`, {
                        active: statusTab === '',
                      })}
                      onClick={() => {
                        setStatusTab('')
                        setOffset(0)
                      }}
                      role='tab'
                    >
                      {t('All')}
                    </a>
                  </li>
                  {advertisementStatuses.map((status) => (
                    <li className='nav-item' key={status.api}>
                      <a
                        className={clsx(`nav-link cursor-pointer`, {
                          active: statusTab === status.api,
                        })}
                        onClick={() => {
                          setStatusTab(status.api)
                          setOffset(0)
                        }}
                        role='tab'
                      >
                        {t(status.display)}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <DataTable
              columns={column}
              data={advertisementList}
              pagination
              paginationServer={true}
              onChangePage={handlePageChange}
              paginationTotalRows={totalRecords}
              paginationPerPage={limit}
              onChangeRowsPerPage={handleRowsPerPageChange}
              paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
              subHeader={true}
              subHeaderComponent={
                <div className='d-flex justify-content-between flex-wrap w-100 my-5 gap-3'>
                  <div className='d-flex align-items-center position-relative my-1'>
                    <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
                    <input
                      type='text'
                      data-kt-user-table-filter='search'
                      className='form-control form-control-solid w-250px ps-14'
                      placeholder={t('Search Name')}
                      value={searchTerm}
                      onChange={(e: any) => searchData(e.target.value)}
                    />
                  </div>
                  <div className='d-flex align-items-center gap-3 flex-wrap'>
                    <div className='d-flex align-items-center gap-2'>
                      <label className='fw-semibold text-muted fs-7 mb-0'>{t('From')}</label>
                      <input
                        type='date'
                        className='form-control form-control-solid w-160px'
                        value={startDate}
                        max={endDate || undefined}
                        onChange={(e) => {
                          setStartDate(e.target.value)
                          setOffset(0)
                        }}
                      />
                    </div>
                    <div className='d-flex align-items-center gap-2'>
                      <label className='fw-semibold text-muted fs-7 mb-0'>{t('To')}</label>
                      <input
                        type='date'
                        className='form-control form-control-solid w-160px'
                        value={endDate}
                        min={startDate || undefined}
                        onChange={(e) => {
                          setEndDate(e.target.value)
                          setOffset(0)
                        }}
                      />
                    </div>
                    {(startDate || endDate) && (
                      <button
                        className='btn btn-sm btn-light-danger'
                        onClick={() => {
                          setStartDate('')
                          setEndDate('')
                          setOffset(0)
                        }}
                      >
                        {t('Clear')}
                      </button>
                    )}
                  </div>
                </div>
              }
              paginationComponentOptions={{
                rowsPerPageText: t('Rows Per Page'),
              }}
            />
          </div>
        </div>
      }
    </>
  )
}

export default ViewSponsoreAds
